import { useEffect, useRef, useState } from "react";

import type { PreviewPlacement } from "@/modules/settings";

export type PreviewPresentation = "split" | "side-overlay" | "bottom";

const SPLIT_PREVIEW_MIN_WIDTH = 1120;
const TOUCH_POINTER_QUERY = "(any-pointer: coarse)";

export function usePreviewPresentation(preference: PreviewPlacement) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isTouchDevice, setIsTouchDevice] = useState(() =>
    typeof window === "undefined"
      ? false
      : window.matchMedia(TOUCH_POINTER_QUERY).matches,
  );

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const updateWidth = () => setContainerWidth(container.clientWidth);
    const observer = new ResizeObserver(updateWidth);

    updateWidth();
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(TOUCH_POINTER_QUERY);
    const updatePointer = () => setIsTouchDevice(mediaQuery.matches);

    mediaQuery.addEventListener("change", updatePointer);
    updatePointer();

    return () => mediaQuery.removeEventListener("change", updatePointer);
  }, []);

  let presentation: PreviewPresentation;

  if (preference === "bottom" || isTouchDevice) {
    presentation = "bottom";
  } else if (containerWidth >= SPLIT_PREVIEW_MIN_WIDTH) {
    presentation = "split";
  } else {
    presentation = "side-overlay";
  }

  return { containerRef, presentation };
}
